import React, { useEffect, useState } from 'react';
import { ShieldCheck, UserPlus, X, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { usersAPI, prospectsAPI } from '@/lib/api';

/**
 * Section "Auditeur" du dossier (onglet Chantier uniquement).
 * - Affiche l'auditeur désigné.
 * - Admin + VP payeur/superviseur du dossier peuvent l'ajouter / le changer / le retirer.
 * - L'auditeur n'a aucune commission (juste un accès lecture + mur Chantier).
 */
export default function ChantierAuditorSection({ prospect, onUpdate }) {
    const { user } = useAuth();
    const [auditors, setAuditors] = useState([]);
    const [selected, setSelected] = useState('');
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);

    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isDossierVp = user?.role === 'vp' && (
        prospect?.dossier_vp_id === user?.id || prospect?.dossier_vp_2_id === user?.id
    );
    const canManage = isAdmin || isDossierVp;

    // Si l'utilisateur courant ne voit le dossier QUE comme auditeur, ne rien afficher de gérable
    const isAuditorView = prospect?.is_auditor_view === true;

    useEffect(() => {
        if (!canManage) return;
        usersAPI.getAuditors()
            .then((r) => setAuditors(r.data || []))
            .catch(() => {});
    }, [canManage]);

    const currentAuditorId = prospect?.dossier_auditeur_id || null;
    const currentAuditorName = prospect?.dossier_auditeur_name;
    // Auditeur effectif = auditeur désigné sur le dossier OU auditeur d'office de l'équipe
    const effectiveAuditorId = prospect?.effective_auditeur_id || currentAuditorId || null;
    const effectiveAuditorName = prospect?.effective_auditeur_name || currentAuditorName;
    const auditorFromTeam = prospect?.auditeur_from_team === true;

    const save = async (auditeurId) => {
        setSaving(true);
        try {
            await prospectsAPI.setAuditor(prospect.id, auditeurId);
            toast.success(auditeurId ? 'Auditeur désigné' : 'Auditeur retiré');
            setEditing(false);
            setSelected('');
            onUpdate?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur lors de l'affectation de l'auditeur");
        } finally {
            setSaving(false);
        }
    };

    // L'auditeur lui-même : simple bandeau informatif
    if (isAuditorView) {
        return (
            <Card className="border-teal-200 bg-teal-50/60" data-testid="auditor-banner">
                <CardContent className="py-3 flex items-center gap-2 text-teal-800 text-sm">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Vous consultez ce dossier en tant qu'<strong>auditeur</strong> — les commissions ne sont pas visibles.</span>
                </CardContent>
            </Card>
        );
    }

    if (!canManage && !effectiveAuditorId) return null;

    return (
        <Card className="border-teal-200" data-testid="chantier-auditor-section">
            <CardContent className="py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                        <ShieldCheck className="w-4 h-4 text-teal-600" />
                        <span className="font-medium text-slate-700">Auditeur du dossier :</span>
                        {effectiveAuditorId ? (
                            <span className="flex items-center gap-2">
                                <span className="text-teal-700 font-semibold" data-testid="current-auditor-name">
                                    {effectiveAuditorName || 'Auditeur désigné'}
                                </span>
                                {auditorFromTeam && (
                                    <span className="inline-flex items-center rounded-full bg-teal-100 text-teal-700 text-[11px] font-medium px-2 py-0.5"
                                          data-testid="auditor-from-team-badge">
                                        Auditeur d'office de l'équipe
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="text-slate-400">Aucun</span>
                        )}
                    </div>

                    {canManage && (
                        <div className="flex items-center gap-2">
                            {!editing && (
                                <Button variant="outline" size="sm" className="text-teal-700 border-teal-300 hover:bg-teal-50"
                                    onClick={() => { setEditing(true); setSelected(currentAuditorId || ''); }}
                                    data-testid="edit-auditor-btn">
                                    <UserPlus className="w-4 h-4 mr-1" />
                                    {currentAuditorId ? 'Changer' : (auditorFromTeam ? 'Désigner pour ce dossier' : 'Désigner un auditeur')}
                                </Button>
                            )}
                            {currentAuditorId && !editing && (
                                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"
                                    onClick={() => save(null)} disabled={saving} data-testid="remove-auditor-btn">
                                    <X className="w-4 h-4 mr-1" /> Retirer
                                </Button>
                            )}
                            {editing && (
                                <>
                                    <Select value={selected} onValueChange={setSelected}>
                                        <SelectTrigger className="w-56" data-testid="auditor-select">
                                            <SelectValue placeholder="Choisir un auditeur" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {auditors.length === 0 && (
                                                <div className="px-3 py-2 text-xs text-slate-400">Aucun utilisateur auditeur</div>
                                            )}
                                            {auditors.map((a) => (
                                                <SelectItem key={a.id} value={a.id} data-testid={`auditor-option-${a.id}`}>
                                                    {a.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700"
                                        onClick={() => save(selected)} disabled={saving || !selected} data-testid="save-auditor-btn">
                                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setSelected(''); }}>
                                        Annuler
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
                {canManage && (
                    <p className="text-xs text-slate-400 mt-2">
                        L'auditeur a accès au dossier (lecture) et au mur Chantier, sans aucune commission visible.
                        {auditorFromTeam && " Cet auditeur est défini par défaut pour toute l'équipe (Réglages d'équipe → Auditeur d'office)."}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
